import * as Sentry from '@sentry/react';
import { getToken } from '../../utils/common/cookies';
import { toSnakeCaseObject } from '../../utils/common/toSnakeCaseObject';
import { JsonObject } from '../../utils/common/toSnakeCaseObject/types';

type RequestInit = globalThis.RequestInit;
type HeadersInit = globalThis.HeadersInit;
type BodyInit = globalThis.BodyInit;
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

type FetchOptions = {
  method: HttpMethod;
  data?: BodyInit | object;
  headers?: HeadersInit;
  isContentTypeEnabled?: boolean;
  queryString?: string;
};

const AUTH_ERROR_STATUS = 401;

/**
 * 統一されたfetchWrapper
 * @param url 完全なURL
 * @param options リクエストオプション
 * @param onUnauthorized サインアウト関数
 * @returns レスポンス
 */
export const fetchWrapper = async (
  url: string,
  options: FetchOptions,
  onUnauthorized?: () => void
): Promise<Response> => {
  const fullUrl = options.queryString ? `${url}?${options.queryString}` : url;
  const config = createRequestConfig(options);
  const response = await fetch(fullUrl, config);

  if (response.status === AUTH_ERROR_STATUS) {
    onUnauthorized?.();
  }

  if (!(response.status >= 200 && response.status < 300)) {
    const clonedResponse = response.clone();
    const error = await clonedResponse.json();
    const errorMessage =
      typeof error.errors === 'string'
        ? error.errors
        : JSON.stringify(error.errors || 'An unknown error occurred');

    // Sentryエラーキャプチャを追加
    Sentry.captureException(new Error(errorMessage), {
      tags: {
        api_url: url,
        status_code: response.status,
        method: options.method,
      },
      extra: {
        response_body: error,
        request_options: options,
      },
    });

    throw new Error(errorMessage);
  }

  return response;
};

const createRequestConfig = ({
  method,
  data,
  headers,
  isContentTypeEnabled = true,
}: FetchOptions): RequestInit => {
  const config: RequestInit = {
    method,
    mode: 'cors',
    cache: 'no-store',
    headers: createHeaders({ isContentTypeEnabled, headers }),
    redirect: 'error',
  };

  if (data) {
    if (typeof data === 'object' && !(data instanceof FormData) && data !== null) {
      // オブジェクトの場合はtoSnakeCaseObjectで変換してJSONにする
      config.body = JSON.stringify(toSnakeCaseObject(data as JsonObject));
    } else {
      // BodyInitの場合はそのまま使用
      config.body = data as BodyInit;
    }
  }

  return config;
};

const createHeaders = ({
  isContentTypeEnabled,
  headers,
}: {
  isContentTypeEnabled: boolean;
  headers?: HeadersInit;
}): HeadersInit => {
  const defaultHeaders: HeadersInit = {};

  // Content-Typeの設定（デフォルトはtrue）
  if (isContentTypeEnabled) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Authorization headerの設定
  const token = getToken();
  if (token) {
    defaultHeaders.Authorization = `Bearer ${token}`;
  }

  return { ...defaultHeaders, ...headers };
};
